/**
 * Layout availability policy with hierarchical overrides.
 *
 * Resolution order (from generic to specific):
 * global -> channel -> type -> set -> product(root) -> product+set(root)
 * -> product -> type+set -> type+product -> set+product -> type+set+product
 *
 * Node shape:
 * {
 *   layouts: [1, 2, 3, {id: 5, iconId: "5"}],
 *   types: { ... },
 *   sets: { ... },
 *   products: { ... }
 * }
 *
 * Root shape:
 * {
 *   global: {layouts: [...]},
 *   products: {                  // channel-independent, keyed by product
 *     AIR: {layouts: [...], sets: {...}},
 *   },
 *   channels: {
 *     releases: {
 *       layouts: [...],          // channel level
 *       types: {...},            // stage/content-type level
 *       sets: {...},             // set level
 *       products: {...},         // product level
 *     }
 *   }
 * }
 *
 * The root `products` node is for a product whose design decides its layouts
 * wherever the product is listed. A channel that must still differ from it
 * keeps a `channels.<channel>` rule, which is more specific and wins.
 */
export const LAYOUT_POLICY = {
  products: {
    // JetBrains Air offers the layouts its Figma frames draw and no others:
    // headline alone, headline over subtitle, headline beside a screenshot
    // (CoverBaker Templates, page "Release: JetBrains Air"). The design is the
    // same in every channel that lists Air, so the rule is stated once here
    // instead of once per channel.
    AIR: {
      sets: {
        "social-media": {
          layouts: [1, 3, 5],
        },
        // One frame: headline over the feature list.
        "whats-new": {
          layouts: [1],
        },
        // Air keeps its own email banners, drawn on one layout. The other
        // products show the Email channel's banners on that channel's layouts.
        emails: {
          layouts: [1],
        },
      },
    },
  },
  channels: {
    // Layout 6 (headline beside a narrow screenshot) is not in the Blog Social
    // Share Figma frames (CoverBaker Templates, "Social_Share_Blog_1280x720
    // layouts"), so the channel does not offer it. Layout 7 (headline + its
    // own icon picker) retired 2026-10-12 — Sergey: "удали этот layout он уже
    // не актуален". The per-template `layoutOption === 7` branches, the
    // `.layout-7` CSS, and the Toolbar's icon selector stay in place
    // (unreachable, not deleted) — the same way layout 6's code was left
    // when it was delisted above.
    social: {
      layouts: [1, 2, 3, 4, 5],
    },
    ads: {
      types: {
        "google-display-network": {
          layouts: [1, 2, 3],
        },
      },
    },
    releases: {
      types: {
        release: {
          sets: {
            "social-media": {
              products: {
                CWM: {
                  layouts: [1, 2, 3, 5],
                },
              },
            },
          },
        },
      },
    },
  },
};
